import React, {useEffect, useMemo, useRef, useState} from 'react';
import {PanResponder, ScrollView, TextInput, View} from 'react-native';

import CCText from '../../../Atoms/CCText';
import type {CourseCover as Cover, CoverCoach} from '../../../Hooks/LmsCourseApi/types';
import {useCoverControls} from '../../../Hooks/LmsCourseApi/useCoverControls';
import {useGeneratedCourse} from '../../../Hooks/LmsCourseApi/useGeneratedCourse';
import CourseCover from '../components/CourseCover';
import {Crop, dragCrop, initials, photoLayout} from '../coverLogic';
import {S, STATUS, T} from '../courseTheme';
import {E} from './editorTheme';
import {Button, Sheet} from './ui';

type Props = {
  bookUuid: string;
  onClose: () => void;
  /** Queue new AI artwork for the background. */
  onRegenerate: () => void;
  regenerating?: boolean;
};

/** Titles a reviewer can put on the badge; "None" shows no badge. */
const TITLES = ['GM', 'IM', 'FM', 'WGM', 'WIM', 'Coach', 'None'];
const FRAME = 160;
const PREVIEW = 480;
const MAX_HOOK = 40;

const sameCrop = (a: Crop, b: Crop) => a.x === b.x && a.y === b.y && a.zoom === b.zoom;
const errorText = (e: unknown) => (e instanceof Error ? e.message : e ? 'That did not save.' : '');

/**
 * The reviewer's controls for a course's thumbnail.
 *
 * The preview is the card's own `CourseCover`, so what is seen here is what
 * students get. The coach photo, its crop, title and name belong to the coach
 * and are reused on every course they teach; the puzzle and the hook belong
 * to this course. Nothing here unpublishes the course.
 */
export default function ThumbnailSheet({bookUuid, onClose, onRegenerate, regenerating}: Props) {
  const {data: course} = useGeneratedCourse(bookUuid);
  const controls = useCoverControls(bookUuid);
  const cover: Cover | null | undefined = course?.cover;
  const coach: CoverCoach | null = cover?.coach ?? null;

  const [crop, setCrop] = useState<Crop>(coach?.crop ?? {x: 0.5, y: 0.4, zoom: 1});
  const [title, setTitle] = useState(coach?.title ?? '');
  const [name, setName] = useState(coach?.name ?? '');
  const [hook, setHook] = useState(cover?.hook ?? '');

  // Back to what the server holds whenever it changes under us — a new
  // upload resets the crop there, and the sheet must show that.
  useEffect(() => {
    if (!coach) return;
    setCrop(coach.crop);
    setTitle(coach.title);
    setName(coach.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coach?.key, coach?.photo_url, coach?.crop.x, coach?.crop.y, coach?.crop.zoom, coach?.title, coach?.name]);
  useEffect(() => setHook(cover?.hook ?? ''), [cover?.hook]);

  // The drag starts from the crop at touch-down, not the crop of each move.
  const dragStart = useRef<Crop>(crop);
  const latest = useRef({crop, coach});
  latest.current = {crop, coach};
  const pan = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        // The sheet scrolls; a drag that started on the photo stays a drag.
        onPanResponderTerminationRequest: () => false,
        onPanResponderGrant: () => {
          dragStart.current = latest.current.crop;
        },
        onPanResponderMove: (_e, g) => {
          const c = latest.current.coach;
          if (!c) return;
          setCrop(dragCrop(dragStart.current, g.dx, g.dy, FRAME, c.photo_width, c.photo_height));
        },
      }),
    [],
  );

  const fileInput = useRef<any>(null);

  const puzzles = useMemo(
    () =>
      (course?.chapters ?? [])
        .flatMap(chapter => chapter.pages ?? [])
        .filter(page => page.content_type === 'chess_position'),
    [course],
  );

  if (!course || !cover) {
    return (
      <Sheet title="Thumbnail" onClose={onClose} width={720}>
        <View style={{padding: S[5]}}>
          <CCText style={{fontSize: T.base, color: E.muted}}>Loading the thumbnail…</CCText>
        </View>
      </Sheet>
    );
  }

  const shownTitle = title === 'None' ? '' : title;
  const preview: Cover = coach
    ? {...cover, coach: {...coach, crop, title: shownTitle, name: name.trim() || coach.name}}
    : cover;
  const coachDirty =
    !!coach &&
    (!sameCrop(crop, coach.crop) || shownTitle !== coach.title || name.trim() !== coach.name);

  const nextPuzzle = () => {
    if (puzzles.length < 2) return;
    const at = puzzles.findIndex(p => p.id === cover.page_id);
    controls.saveCover.mutate({page_id: puzzles[(at + 1) % puzzles.length].id}, {});
  };

  const photo = coach?.photo_url
    ? photoLayout(FRAME, coach.photo_width, coach.photo_height, crop)
    : null;
  const failure =
    errorText(controls.upload.error) ||
    errorText(controls.saveCoach.error) ||
    errorText(controls.removePhoto.error) ||
    errorText(controls.saveCover.error);

  return (
    <Sheet title="Thumbnail" onClose={onClose} width={720}>
      {/* Taller than most windows: the Sheet caps its height and clips, so
          the content scrolls inside it. */}
      <ScrollView contentContainerStyle={{padding: S[5], gap: S[4]}}>
        <CCText style={{fontSize: T.sm, color: E.muted, lineHeight: 20}}>
          What students see on the course card. The background is painted from the course; the
          title, a puzzle and the coach are laid on top.
        </CCText>

        <View style={{width: PREVIEW, borderRadius: 12, overflow: 'hidden', alignSelf: 'center'}}>
          <CourseCover cover={preview} thumbnail={course.book.thumbnail} width={PREVIEW} />
        </View>

        <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: S[2], justifyContent: 'center'}}>
          {puzzles.length > 1 ? (
            <Button label="Change puzzle" icon="swap-horizontal" onPress={nextPuzzle}
              disabled={!!controls.saveCover.isLoading} />
          ) : null}
          <Button label="Regenerate artwork" icon="color-palette-outline" onPress={onRegenerate}
            disabled={!!regenerating} />
        </View>

        <View style={{gap: S[2]}}>
          <CCText style={{fontSize: T.sm, fontWeight: '700', color: E.ink}}>Hook</CCText>
          <View style={{flexDirection: 'row', gap: S[2], alignItems: 'center'}}>
            <TextInput
              value={hook}
              onChangeText={t => setHook(t.slice(0, MAX_HOOK))}
              accessibilityLabel="Hook"
              placeholder="Empty: written from the puzzle"
              style={{
                flex: 1,
                minHeight: 44,
                borderWidth: 1,
                borderColor: E.inputLine,
                borderRadius: 10,
                paddingHorizontal: S[3],
                fontSize: T.base,
                color: E.ink,
              }}
            />
            <Button
              label="Save hook"
              variant="accent"
              onPress={() => controls.saveCover.mutate({hook: hook.trim()}, {})}
              disabled={!!controls.saveCover.isLoading}
            />
          </View>
        </View>

        {coach ? (
          <View style={{gap: S[3], borderTopWidth: 1, borderTopColor: E.line, paddingTop: S[4]}}>
            <CCText style={{fontSize: T.sm, fontWeight: '700', color: E.ink}}>
              Coach photo · used on every course by this coach
            </CCText>
            <View style={{flexDirection: 'row', gap: S[4], alignItems: 'center', flexWrap: 'wrap'}}>
              <View
                {...(photo ? pan.panHandlers : {})}
                accessibilityLabel={photo ? 'Drag to centre the face' : 'No photo yet'}
                style={{
                  width: FRAME,
                  height: FRAME,
                  borderRadius: FRAME / 2,
                  overflow: 'hidden',
                  backgroundColor: '#3B312A',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 3,
                  borderColor: E.accent,
                  cursor: photo ? 'grab' : 'default',
                } as any}>
                {photo ? (
                  <img
                    src={coach.photo_url}
                    alt={`${coach.title} ${coach.name}`.trim()}
                    draggable={false}
                    style={{position: 'absolute', ...photo, userSelect: 'none', pointerEvents: 'none'}}
                  />
                ) : (
                  <CCText style={{fontSize: 40, fontWeight: '700', color: '#FFFFFF'}}>
                    {initials(coach.name)}
                  </CCText>
                )}
              </View>

              <View style={{gap: S[2], flex: 1, minWidth: 220}}>
                {photo ? (
                  <>
                    <CCText style={{fontSize: T.sm, color: E.body}}>
                      Drag the photo to centre the face.
                    </CCText>
                    <input
                      type="range"
                      aria-label="Zoom"
                      min={100}
                      max={400}
                      step={5}
                      value={Math.round(crop.zoom * 100)}
                      onChange={(e: any) => setCrop(c => ({...c, zoom: Number(e.target.value) / 100}))}
                      style={{width: 220, accentColor: E.accent}}
                    />
                  </>
                ) : (
                  <CCText style={{fontSize: T.sm, color: E.body}}>
                    No photo yet: the thumbnail shows initials.
                  </CCText>
                )}
                <View style={{flexDirection: 'row', gap: S[2], flexWrap: 'wrap'}}>
                  <Button
                    label={photo ? 'Replace photo' : 'Upload photo'}
                    variant="accent"
                    icon="cloud-upload-outline"
                    disabled={!!controls.upload.isLoading}
                    onPress={() => fileInput.current?.click?.()}
                  />
                  {photo ? (
                    <Button
                      label="Remove photo"
                      variant="quiet"
                      disabled={!!controls.removePhoto.isLoading}
                      onPress={() => controls.removePhoto.mutate(coach.key, {})}
                    />
                  ) : null}
                </View>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{display: 'none'}}
                  onChange={(e: any) => {
                    const file = e.target.files?.[0];
                    if (file) controls.upload.mutate({coach: coach.key, photo: file}, {});
                    // Choosing the same file again must still fire a change.
                    e.target.value = '';
                  }}
                />
              </View>
            </View>

            <View style={{gap: S[2]}}>
              <CCText style={{fontSize: T.xs, fontWeight: '700', color: E.muted}}>TITLE</CCText>
              <View style={{flexDirection: 'row', gap: S[1], flexWrap: 'wrap'}}>
                {TITLES.map(t => (
                  <Button
                    key={t}
                    label={t}
                    variant={(shownTitle || 'None') === t ? 'primary' : 'quiet'}
                    onPress={() => setTitle(t === 'None' ? '' : t)}
                  />
                ))}
              </View>
              <TextInput
                value={name}
                onChangeText={t => setName(t.slice(0, 80))}
                accessibilityLabel="Name on the thumbnail"
                style={{
                  minHeight: 44,
                  borderWidth: 1,
                  borderColor: E.inputLine,
                  borderRadius: 10,
                  paddingHorizontal: S[3],
                  fontSize: T.base,
                  color: E.ink,
                }}
              />
            </View>

            <View style={{flexDirection: 'row', justifyContent: 'flex-end'}}>
              <Button
                label="Save coach"
                variant="accent"
                icon="checkmark"
                disabled={!coachDirty || !!controls.saveCoach.isLoading}
                onPress={() =>
                  controls.saveCoach.mutate(
                    {coach: coach.key, crop, display_title: shownTitle, display_name: name.trim()},
                    {},
                  )
                }
              />
            </View>
          </View>
        ) : (
          <CCText style={{fontSize: T.sm, color: E.muted}}>
            This course names no coach, so the thumbnail shows no coach photo.
          </CCText>
        )}

        {failure ? (
          <CCText accessibilityRole="alert" style={{fontSize: T.sm, color: STATUS.bad.fg}}>
            {failure}
          </CCText>
        ) : null}
      </ScrollView>
    </Sheet>
  );
}
